import { useEffect, useRef } from 'react';
import type { ActiveTool } from '../../toolbar/use-canvas-tool';
import { DEFAULT_STICKY_COLOR, DEFAULT_FREEDRAW_COLOR, DEFAULT_FREEDRAW_SIZE } from '@/features/canvas/utils/constants';

interface ToolShortcutActions {
  selectTool: (tool: ActiveTool) => void;
  reset: () => void;
}

export function useToolShortcut({ selectTool, reset }: ToolShortcutActions) {
  const actionsRef = useRef({ selectTool, reset });
  actionsRef.current = { selectTool, reset };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      const key = e.key;
      if (key === 'Escape' || key === 'v' || key === 'V') {
        actionsRef.current.reset();
      } else if (key === 'h' || key === 'H') {
        actionsRef.current.selectTool({ kind: 'hand' });
      } else if (key === 'r' || key === 'R') {
        actionsRef.current.selectTool({ kind: 'shape', shapeKind: 'rectangle' });
      } else if (key === 's' || key === 'S') {
        actionsRef.current.selectTool({ kind: 'sticky', stickyColor: DEFAULT_STICKY_COLOR });
      } else if (key === 't' || key === 'T') {
        actionsRef.current.selectTool({ kind: 'text' });
      } else if (key === 'f' || key === 'F') {
        actionsRef.current.selectTool({ kind: 'frame', size: null });
      } else if (key === 'd' || key === 'D') {
        actionsRef.current.selectTool({ kind: 'freedraw', strokeColor: DEFAULT_FREEDRAW_COLOR, strokeWidth: DEFAULT_FREEDRAW_SIZE });
      } else if (key === 'l' || key === 'L') {
        actionsRef.current.selectTool({ kind: 'connector' });
      } else {
        return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
}
